import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Avatar
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import { toast } from 'react-toastify';
import {
  getProductBrands,
  addProductBrand,
  updateProductBrand,
  deleteProductBrand
} from '../../productbrand/services/productBrandService';

const ProductBrand = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [formData, setFormData] = useState({
    Id: 0,
    BrandName: '',
    Description: '',
    LogoUrl: '',
    Website: ''
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    setLoading(true);
    try {
      const response = await getProductBrands();
      const brandsData = response?.payload || response || [];
      setBrands(Array.isArray(brandsData) ? brandsData : []);
    } catch (error) {
      console.error('Error fetching brands:', error);
      toast.error('Failed to load product brands');
      setBrands([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (brand = null) => {
    if (brand) {
      setEditMode(true);
      setFormData({
        Id: brand.Id,
        BrandName: brand.BrandName || '',
        Description: brand.Description || '',
        LogoUrl: brand.LogoUrl || '',
        Website: brand.Website || ''
      });
    } else {
      setEditMode(false);
      setFormData({
        Id: 0,
        BrandName: '',
        Description: '',
        LogoUrl: '',
        Website: ''
      });
    }
    setErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      Id: 0,
      BrandName: '',
      Description: '',
      LogoUrl: '',
      Website: ''
    });
    setErrors({});
  };

  const validateForm = () => {
    const newErrors = {};

    // Brand Name - Required
    if (!formData.BrandName.trim()) {
      newErrors.BrandName = 'Brand name is required';
    } else if (formData.BrandName.length > 100) {
      newErrors.BrandName = 'Brand name cannot exceed 100 characters';
    }

    // Description - Optional
    if (formData.Description && formData.Description.length > 500) {
      newErrors.Description = 'Description cannot exceed 500 characters';
    }

    // Logo URL - Optional
    if (formData.LogoUrl) {
      if (formData.LogoUrl.length > 300) {
        newErrors.LogoUrl = 'Logo URL cannot exceed 300 characters';
      } else {
        try {
          const url = new URL(formData.LogoUrl);
          if (!['http:', 'https:'].includes(url.protocol)) {
            newErrors.LogoUrl = 'URL must start with http:// or https://';
          }
        } catch {
          newErrors.LogoUrl = 'Please enter a valid URL';
        }
      }
    }

    // Website - Optional
    if (formData.Website) {
      if (formData.Website.length > 200) {
        newErrors.Website = 'Website URL cannot exceed 200 characters';
      } else {
        try {
          const url = new URL(formData.Website);
          if (!['http:', 'https:'].includes(url.protocol)) {
            newErrors.Website = 'URL must start with http:// or https://';
          }
        } catch {
          newErrors.Website = 'Please enter a valid URL';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the form errors');
      return;
    }

    try {
      const brandData = {
        Id: formData.Id,
        BrandName: formData.BrandName.trim(),
        Description: formData.Description?.trim() || null,
        LogoUrl: formData.LogoUrl?.trim() || null,
        Website: formData.Website?.trim() || null
      };

      if (editMode) {
        await updateProductBrand(brandData);
        toast.success('Brand updated successfully');
      } else {
        await addProductBrand(brandData);
        toast.success('Brand added successfully');
      }

      handleCloseDialog();
      fetchBrands();
    } catch (error) {
      console.error('Error saving brand:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(editMode ? 'Failed to update brand' : 'Failed to add brand');
      }
    }
  };

  const handleDelete = async (brandId) => {
    if (window.confirm('Are you sure you want to delete this brand?')) {
      try {
        await deleteProductBrand(brandId);
        toast.success('Brand deleted successfully');
        fetchBrands();
      } catch (error) {
        console.error('Error deleting brand:', error);
        toast.error('Failed to delete brand');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const filteredBrands = brands.filter(brand =>
    brand.BrandName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BrandingWatermarkIcon sx={{ fontSize: 32, color: '#667eea' }} />
          <Box>
            <Typography variant="h5" fontWeight="bold" color="#667eea">
              Product Brands
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage your product brands
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)',
            }
          }}
        >
          Add Brand
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid rgba(0,0,0,0.08)' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.08)', bgcolor: '#fafafa' }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search brands..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                bgcolor: 'white'
              }
            }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Logo</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Brand Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Website</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBrands.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {searchTerm ? 'No brands found matching your search' : 'No brands available. Click "Add Brand" to create one.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredBrands.map((brand) => (
                  <TableRow key={brand.Id} hover>
                    <TableCell>
                      {brand.LogoUrl ? (
                        <Avatar
                          src={brand.LogoUrl}
                          alt={brand.BrandName}
                          variant="rounded"
                          sx={{ width: 40, height: 40 }}
                        />
                      ) : (
                        <Avatar variant="rounded" sx={{ width: 40, height: 40, bgcolor: '#667eea' }}>
                          <BrandingWatermarkIcon />
                        </Avatar>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {brand.BrandName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
                        {brand.Description || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {brand.Website ? (
                        <a 
                          href={brand.Website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: '#667eea', textDecoration: 'none' }}
                        >
                          Visit
                        </a>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenDialog(brand)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDelete(brand.Id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {editMode ? 'Edit Brand' : 'Add New Brand'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              fullWidth
              label="Brand Name *"
              name="BrandName"
              value={formData.BrandName}
              onChange={handleInputChange}
              error={!!errors.BrandName}
              helperText={errors.BrandName || 'Required (max 100 characters)'}
              inputProps={{ maxLength: 100 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />

            <TextField
              fullWidth
              label="Description"
              name="Description"
              value={formData.Description}
              onChange={handleInputChange}
              error={!!errors.Description}
              helperText={errors.Description || 'Optional (max 500 characters)'}
              multiline
              rows={3}
              inputProps={{ maxLength: 500 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />

            <TextField
              fullWidth
              label="Logo URL"
              name="LogoUrl"
              value={formData.LogoUrl}
              onChange={handleInputChange}
              error={!!errors.LogoUrl}
              helperText={errors.LogoUrl || 'Optional (max 300 characters)'}
              placeholder="https://example.com/logo.png"
              inputProps={{ maxLength: 300 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />

            <TextField
              fullWidth
              label="Website"
              name="Website"
              value={formData.Website}
              onChange={handleInputChange}
              error={!!errors.Website}
              helperText={errors.Website || 'Optional (max 200 characters)'}
              placeholder="https://example.com"
              inputProps={{ maxLength: 200 }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDialog} sx={{ borderRadius: 1.5 }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              borderRadius: 1.5,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            {editMode ? 'Update' : 'Add'} Brand
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductBrand;